import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuthController, useRebaseClient } from "@rebasepro/app";
import type { RebaseClient } from "@rebasepro/types";
import {
    Button as RebaseButton,
    Card as RebaseCard,
    Chip as RebaseChip,
    TextField as RebaseTextField,
    Typography as RebaseTypography
} from "@rebasepro/ui";

import { threadTitle, threadWith, type ThreadRow } from "./_email_shared";
import { ConversationLink } from "./ConversationPanel";
import { sinceLabel } from "./_prospects_shared";

const Card = RebaseCard as React.ComponentType<any>;
const Button = RebaseButton as React.ComponentType<any>;
const TextField = RebaseTextField as React.ComponentType<any>;
const Chip = RebaseChip as React.ComponentType<any>;
const Typography = RebaseTypography as React.ComponentType<any>;

/**
 * The pieces every entity cover is made of.
 *
 * A "cover" is the tab a record opens on: what this thing is and what to do
 * about it, read rather than edited, with the form one tab to the right. There
 * are three — candidates, vacancies and companies — and they have to look like
 * one product, so the label-and-value line, the chip list and the "have we
 * spoken" block live here instead of three times over.
 *
 * Deliberately not a `Cover` component with slots. The three records have
 * genuinely different shapes and a frame that tried to hold all of them would be
 * a worse version of each; what is shared is the vocabulary, not the layout.
 */

/** The outer frame: one column, one comfortable measure, on every cover. */
export const coverRootCls = "flex flex-col gap-5 p-4 max-w-4xl";

/** A labelled value. Renders nothing when there is nothing to say. */
export function Fact({ label, children }: { label: string; children?: React.ReactNode }) {
    if (children === null || children === undefined || children === false || children === "") return null;
    return (
        <div className="flex flex-col gap-0.5 min-w-0">
            <Typography variant="body2" color="secondary">{label}</Typography>
            <div className="text-sm break-words">{children}</div>
        </div>
    );
}

/**
 * The canonical form of a free-text answer, falling back to what was typed.
 *
 * `*_canon` is what the normalizer made of somebody's own words and is what
 * search, coverage and the applicants panel read, so showing it keeps these
 * screens agreeing with the rest of the product. Falling back to the raw answer
 * keeps a row the normalizer has not reached from reading as a record with no
 * certifications at all.
 */
export function textList(canon: unknown, raw?: unknown): string[] {
    const source = Array.isArray(canon) && canon.length ? canon : raw;
    if (!Array.isArray(source)) return [];
    return source.map(v => String(v ?? "").trim()).filter(Boolean);
}

/** Free text as chips. An empty list draws nothing — guard the label too. */
export function ChipList({
    values,
    colorScheme = "grayLighter"
}: {
    values: (string | null | undefined)[] | null | undefined;
    colorScheme?: string;
}) {
    const items = (values ?? []).map(v => (v ?? "").trim()).filter(Boolean);
    if (items.length === 0) return null;
    return (
        <div className="flex flex-wrap gap-1">
            {items.map((v, i) => (
                <Chip key={`${v}-${i}`} size="small" colorScheme={colorScheme}>{v}</Chip>
            ))}
        </div>
    );
}

/** How many conversations a cover shows before it stops being a summary. */
const THREADS_SHOWN = 3;

/**
 * "Have we spoken to them, and who spoke last."
 *
 * The thing every cover was missing hardest: a record we have written to four
 * times read exactly like one nobody has ever contacted. Three lines here
 * answer the question that changes what you do next, and each one opens its
 * conversation in a side panel over the cover — read, answered, and closed
 * without leaving the record. The full list is the record's own "Correo" tab.
 *
 * `employerKey` is the same reconciliation `EntityEmailPanel` does: outreach to
 * an employer is filed against a name-derived prospect key, and a company that
 * has been in a thread for three months would otherwise read as never contacted
 * on its own screen. See drizzle/0044.
 */
export function RecentThreads({
    kind,
    entityId,
    employerKey,
    emptyLabel,
    refresh = 0
}: {
    kind: "talent" | "company";
    entityId: string;
    employerKey?: string | null;
    emptyLabel: string;
    /**
     * Bumped by the cover when it sends something itself. The composer on a
     * cover sat two blocks above this one, and after "Enviado" this block
     * still listed the conversations as they were before it.
     */
    refresh?: number;
}) {
    const client = useRebaseClient<RebaseClient>();
    const data = (client as any)?.data;

    const [threads, setThreads] = useState<ThreadRow[] | null>(null);
    const [failed, setFailed] = useState(false);

    /**
     * Whose conversations the last request was for.
     *
     * The queue keeps this component mounted and swaps the record underneath it,
     * so without both halves of this guard — clearing on the way in, and refusing
     * a late answer on the way out — clicking from one candidate to the next
     * shows the *previous* person's correspondence under the new person's name.
     * Out of order, it stays there. This is the one block on the cover where
     * stale data is not merely late: it is somebody else's private mail.
     */
    const wanted = useRef<string | null>(null);

    useEffect(() => {
        setThreads(null);
        setFailed(false);
    }, [entityId, employerKey]);

    const load = useCallback(async () => {
        if (!data || !entityId) return;
        wanted.current = entityId;
        try {
            // Two queries rather than one: the filter syntax is a conjunction of
            // per-field comparisons and cannot express "this record OR that
            // key". Merged by id, because a thread can match both once somebody
            // links it by hand.
            const queries = [
                data.collection("email-threads").find({
                    where: { entity_type: ["==", kind], entity_id: ["==", entityId] },
                    orderBy: ["last_message_at", "desc"],
                    limit: THREADS_SHOWN + 1
                })
            ];
            if (employerKey) {
                queries.push(data.collection("email-threads").find({
                    where: { entity_type: ["==", "prospect"], employer_key: ["==", employerKey] },
                    orderBy: ["last_message_at", "desc"],
                    limit: THREADS_SHOWN + 1
                }));
            }
            const pages = await Promise.all(queries);
            if (wanted.current !== entityId) return;
            const seen = new Map<string, ThreadRow>();
            for (const page of pages) {
                for (const thread of ((page?.data ?? []) as ThreadRow[])) {
                    if (thread?.id) seen.set(String(thread.id), thread);
                }
            }
            setThreads([...seen.values()].sort(
                (a, b) => (b.last_message_at ?? "").localeCompare(a.last_message_at ?? "")
            ));
            setFailed(false);
        } catch {
            if (wanted.current !== entityId) return;
            // `null` is also the state before the first answer arrives, and a
            // panel that says "no se pudieron leer" for the half second it is
            // reading them is a bug report waiting to be filed.
            setThreads(null);
            setFailed(true);
        }
    // `refresh` is not read, only listened to: a new value is a new load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, kind, entityId, employerKey, refresh]);

    useEffect(() => { void load(); }, [load]);

    return (
        <div className="flex flex-col gap-3">
            <Typography variant="subtitle2" component="h3">
                {/* No count until there is one to give: `(0)` while the query
                    is in flight is a number, and a number reads as an answer. */}
                {threads === null
                    ? "Correo"
                    : `Correo (${threads.length}${threads.length > THREADS_SHOWN ? "+" : ""})`}
            </Typography>
            {failed && (
                <Typography variant="body2" color="secondary">
                    No se pudieron leer las conversaciones.
                </Typography>
            )}
            {threads?.length === 0 && (
                <Typography variant="body2" color="secondary">{emptyLabel}</Typography>
            )}
            {threads?.slice(0, THREADS_SHOWN).map(thread => (
                <Card key={thread.id} className="p-3 flex flex-col gap-1">
                    <div className="flex items-start gap-2 flex-wrap">
                        <Typography variant="body2" className="grow font-medium">
                            {/* `load` again once the panel has written
                                anything: a reply sent from it would otherwise
                                leave this card saying "esperando respuesta". */}
                            <ConversationLink id={thread.id} onChanged={load}>{threadTitle(thread)}</ConversationLink>
                        </Typography>
                        {thread.last_direction === "inbound" && (
                            // The one distinction that changes what you do: they
                            // wrote last, so the ball is here.
                            <Chip size="small" colorScheme="redLighter">esperando respuesta</Chip>
                        )}
                    </div>
                    <Typography variant="body2" color="secondary">
                        {threadWith(thread)} · {thread.message_count ?? 0} mensajes · {sinceLabel(thread.last_message_at)}
                    </Typography>
                </Card>
            ))}
            {threads && threads.length > THREADS_SHOWN && (
                <Typography variant="body2" color="secondary">
                    Las conversaciones completas están en la pestaña «Correo».
                </Typography>
            )}
        </div>
    );
}

// ── Notes ────────────────────────────────────────────────────────────

interface NoteRow {
    id: string;
    body?: string | null;
    actor?: string | null;
    created_at?: string | null;
}

/**
 * What the team wrote down about this record, newest first, and a box to
 * write the next one.
 *
 * Append-only by design (drizzle/0054): a note is what somebody thought at
 * the time, and there is no edit. The author is stamped from the session,
 * the same way `prospect_activity.actor` is — an entry with no author is a
 * log that cannot answer "who spoke to her".
 *
 * The same stale-answer guard as `RecentThreads`, for the same reason: the
 * queue swaps the record under a mounted cover, and a note about the
 * previous person under the next person's name is a real mistake.
 */
export function Notes({ kind, entityId }: { kind: "talent" | "company"; entityId: string }) {
    const client = useRebaseClient<RebaseClient>();
    const auth = useAuthController();
    const data = (client as any)?.data;

    const [notes, setNotes] = useState<NoteRow[] | null>(null);
    const [draft, setDraft] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const wanted = useRef<string | null>(null);

    useEffect(() => { setNotes(null); setDraft(""); setError(""); }, [entityId]);

    const load = useCallback(async () => {
        if (!data || !entityId) return;
        wanted.current = entityId;
        try {
            const page = await data.collection("notes").find({
                where: { entity_type: ["==", kind], entity_id: ["==", entityId] },
                orderBy: ["created_at", "desc"],
                limit: 50
            });
            if (wanted.current !== entityId) return;
            setNotes((page?.data ?? []) as NoteRow[]);
        } catch {
            if (wanted.current !== entityId) return;
            setNotes([]);
        }
    }, [data, kind, entityId]);

    useEffect(() => { void load(); }, [load]);

    const save = useCallback(async () => {
        const body = draft.trim();
        if (!data || !entityId || !body) return;
        setSaving(true);
        setError("");
        try {
            await data.collection("notes").create({
                entity_type: kind,
                entity_id: entityId,
                body,
                actor: (auth as any)?.user?.email || (auth as any)?.user?.uid || null
            });
            setDraft("");
            await load();
        } catch (err: any) {
            setError(err?.message || "No se pudo guardar la nota");
        } finally {
            setSaving(false);
        }
    }, [data, entityId, kind, draft, auth, load]);

    return (
        <div className="flex flex-col gap-3">
            <Typography variant="subtitle2" component="h3">
                Notas{notes && notes.length > 0 ? ` (${notes.length})` : ""}
            </Typography>
            <div className="flex flex-col gap-2">
                <TextField
                    size="small"
                    label="Apuntar algo"
                    multiline
                    minRows={2}
                    value={draft}
                    disabled={saving}
                    onChange={(e: any) => setDraft(e.target.value)}
                    onKeyDown={(e: any) => {
                        // Cmd/Ctrl+Enter saves; Enter alone is a new line, as
                        // in every notes box anyone has used.
                        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void save(); }
                    }}
                />
                <div className="flex items-center gap-2">
                    <Button size="small" variant="outlined" disabled={saving || !draft.trim()} onClick={() => void save()}>
                        Guardar nota
                    </Button>
                    {error && <Typography variant="body2" color="secondary">{error}</Typography>}
                </div>
            </div>
            {notes?.length === 0 && (
                <Typography variant="body2" color="secondary">Nadie ha apuntado nada todavía.</Typography>
            )}
            {notes?.map(note => (
                <Card key={note.id} className="p-3 flex flex-col gap-1">
                    <Typography variant="body2" className="whitespace-pre-wrap break-words">{note.body}</Typography>
                    <Typography variant="caption" color="secondary">
                        {[note.actor, sinceLabel(note.created_at)].filter(Boolean).join(" · ")}
                    </Typography>
                </Card>
            ))}
        </div>
    );
}

// ── Tags ─────────────────────────────────────────────────────────────

/** One spelling per tag: lowercased, trimmed, no duplicates, no empties. */
export function normalizeTags(values: readonly string[]): string[] {
    const out: string[] = [];
    for (const raw of values) {
        const tag = raw.trim().toLowerCase().replace(/\s+/g, " ");
        if (tag && !out.includes(tag)) out.push(tag);
    }
    return out;
}

/**
 * The labels the team puts on a person, editable in place.
 *
 * Free text on purpose. A fixed list would be right the day it was written
 * and wrong the week a new process opened; a tag typed twice with two
 * spellings is the cost, and `normalizeTags` pays most of it. Never shown to
 * the candidate — see the column comment in drizzle/0054.
 */
export function Tags({
    values,
    onChange,
    disabled
}: {
    values: string[];
    onChange: (next: string[]) => Promise<void> | void;
    disabled?: boolean;
}) {
    const [draft, setDraft] = useState("");
    const [busy, setBusy] = useState(false);

    const commit = useCallback(async (next: string[]) => {
        setBusy(true);
        try { await onChange(normalizeTags(next)); } finally { setBusy(false); }
    }, [onChange]);

    const add = useCallback(async () => {
        const tag = draft.trim();
        if (!tag) return;
        setDraft("");
        await commit([...values, tag]);
    }, [draft, values, commit]);

    return (
        <div className="flex flex-col gap-2">
            <Typography variant="subtitle2" component="h3">Etiquetas</Typography>
            <div className="flex flex-wrap items-center gap-1.5">
                {values.map(tag => (
                    <Chip key={tag} size="small" colorScheme="grayLighter">
                        {tag}
                        <button
                            type="button"
                            aria-label={`Quitar ${tag}`}
                            disabled={disabled || busy}
                            onClick={() => void commit(values.filter(t => t !== tag))}
                            className="ml-1 opacity-60 hover:opacity-100"
                        >×</button>
                    </Chip>
                ))}
                <input
                    type="text"
                    aria-label="Nueva etiqueta"
                    placeholder={values.length ? "añadir…" : "p. ej. terna mineros, interesada 360"}
                    value={draft}
                    disabled={disabled || busy}
                    onChange={e => setDraft(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); void add(); } }}
                    onBlur={() => { if (draft.trim()) void add(); }}
                    className="text-sm bg-transparent border-b border-transparent focus:border-current outline-none px-1 py-0.5 min-w-[12rem]"
                />
            </div>
        </div>
    );
}
